import { useState, useMemo, useEffect } from 'react';
import { TrendingUp, Clock, CheckCircle2, AlertCircle, BarChart3, CalendarDays } from 'lucide-react';
import { KPI } from '@/components/ui/kpi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DemoProject, ProjectBudgetItem } from '@/data/mockProjectsData';
import { EBProjectsBudgetComposite } from '@/services/api';

// --- Props ---

interface ResourceBudgetTabProps {
  project: DemoProject;
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetFetched: boolean;
  hasLiveBudget: boolean;
}

// --- Normalised row type ---

interface NormalisedRoleRow {
  id: string;
  role: string;
  budgetedHours: number;
  actualHours: number;
  remainingHours: number;
  costRate: number;
  billingRate: number;
  monthlyHoursRaw?: string;
  monthlyHoursMap?: Record<string, number>;
}

// --- Helpers (mirrors EBProjects edit-resource-budget-form.tsx) ---

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Build project months as yyyy-MM strings (same as EBProjects buildProjectMonthsRb).
 * Falls back to 12 months of the current year when dates are missing/invalid.
 */
function buildProjectMonths(startDate?: string, endDate?: string): string[] {
  if (startDate && endDate) {
    try {
      const s = new Date(startDate);
      const e = new Date(endDate);
      if (s.getFullYear() > 1900 && e.getFullYear() > 1900 && e >= s) {
        const months: string[] = [];
        const current = new Date(s.getFullYear(), s.getMonth(), 1);
        const last = new Date(e.getFullYear(), e.getMonth(), 1);
        while (current <= last) {
          months.push(`${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`);
          current.setMonth(current.getMonth() + 1);
        }
        if (months.length > 0) return months;
      }
    } catch { /* fall through */ }
  }
  const year = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) =>
    `${year}-${String(i + 1).padStart(2, '0')}`,
  );
}

/**
 * Parse monthlyHours JSON and pad/truncate to match monthCount.
 * Same pattern as EBProjects:
 *   [...role.monthlyHours, ...Array(max(0, months.length - arr.length)).fill(0)].slice(0, months.length)
 */
function parseMonthlyHoursArray(json: string, monthCount: number): number[] {
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      return [...parsed, ...Array(Math.max(0, monthCount - parsed.length)).fill(0)].slice(0, monthCount);
    }
    if (parsed && typeof parsed === 'object') {
      const keys = Object.keys(parsed).sort((a, b) => Number(a) - Number(b));
      const arr = keys.map(k => (typeof parsed[k] === 'number' ? parsed[k] : 0));
      return [...arr, ...Array(Math.max(0, monthCount - arr.length)).fill(0)].slice(0, monthCount);
    }
  } catch { /* ignore */ }
  return Array(monthCount).fill(0);
}

function pctUsed(actual: number, budget: number): number {
  if (budget <= 0) return 0;
  return Math.min(Math.round((actual / budget) * 100), 999);
}

function statusFromPct(pct: number): { label: string; accent: string } {
  if (pct >= 100) return { label: 'Over Budget', accent: 'destructive' };
  if (pct >= 85)  return { label: 'At Risk',     accent: 'warning' };
  return                  { label: 'On Track',    accent: 'success' };
}

// --- Sub-components ---

function StatusBadge({ pct }: { pct: number }) {
  const { label, accent } = statusFromPct(pct);
  return (
    <Badge
      variant="accent"
      accent={accent as 'destructive' | 'warning' | 'success'}
      className="text-[11px] px-2 py-0.5"
    >
      {label}
    </Badge>
  );
}

function PctBar({ pct }: { pct: number }) {
  const color =
    pct >= 100 ? 'bg-[var(--accent-destructive-500)]' :
    pct >= 85  ? 'bg-[var(--accent-warning-500)]' :
                 'bg-[var(--accent-teal-500)]';
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="text-xs tabular-nums font-medium w-8 text-right">{pct}%</span>
    </div>
  );
}

// --- Unified monthly grid (mirrors EBProjects index-based pattern) ---

interface UnifiedMonthlyGridProps {
  rows: NormalisedRoleRow[];
  months: string[]; // yyyy-MM strings from buildProjectMonths
}

function UnifiedMonthlyGrid({ rows, months }: UnifiedMonthlyGridProps) {
  // Parse each role's monthlyHours into number[] matching months.length
  // Same pattern as EBProjects:
  //   [...role.monthlyHours, ...Array(max(0, months.length - arr.length)).fill(0)].slice(0, months.length)
  const roleHours = useMemo(
    () => rows.map(r => ({
      row: r,
      monthlyHours: r.monthlyHoursRaw
        ? parseMonthlyHoursArray(r.monthlyHoursRaw, months.length)
        : Array(months.length).fill(0) as number[],
    })),
    [rows, months],
  );

  // Column totals (same as EBProjects grandMonthlyTotals)
  const colTotals = useMemo(() => {
    const totals = Array(months.length).fill(0) as number[];
    roleHours.forEach(({ monthlyHours }) => {
      monthlyHours.forEach((h, i) => { totals[i] += h; });
    });
    return totals;
  }, [roleHours, months.length]);

  const budgetGrandTotal = rows.reduce((s, r) => s + r.budgetedHours, 0);

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      {/* Grid header */}
      <div className="bg-muted/40 border-b border-border px-4 py-2.5 flex items-center gap-2">
        <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Monthly Hour Spread - All Roles
        </span>
        <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
          {budgetGrandTotal.toFixed(0)}h across {months.length} month{months.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Scrollable grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/20">
              {/* Sticky role column */}
              <th className="text-left text-[11px] font-semibold text-muted-foreground px-4 py-2.5
                             min-w-[160px] sticky left-0 bg-muted z-10 whitespace-nowrap border-r border-border/60">
                Role
              </th>
              {months.map(m => {
                const [year, month] = m.split('-');
                return (
                  <th
                    key={m}
                    className="text-center text-[11px] font-semibold text-muted-foreground
                               px-3 py-2.5 min-w-[72px] whitespace-nowrap"
                  >
                    {SHORT_MONTHS[parseInt(month, 10) - 1]}/{String(year).slice(-2)}
                  </th>
                );
              })}
              {/* Sticky total column */}
              <th className="text-right text-[11px] font-semibold text-muted-foreground
                             px-4 py-2.5 min-w-[90px] whitespace-nowrap
                             sticky right-0 bg-muted z-10 border-l border-border/60">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {roleHours.map(({ row, monthlyHours }) => (
              <tr key={row.id} className="border-b border-border/40 hover:bg-muted/10 transition-colors">
                {/* Role name - sticky */}
                <td className="px-4 py-2.5 font-medium text-xs sticky left-0 bg-background z-10
                               whitespace-nowrap border-r border-border/60">
                  {row.role}
                </td>
                {/* Monthly cells - index-based access (same as EBProjects) */}
                {monthlyHours.map((hrs, monthIdx) => (
                  <td key={months[monthIdx]} className="text-center tabular-nums px-3 py-2.5 text-xs">
                    {hrs > 0
                      ? <span className="font-medium">{hrs % 1 === 0 ? hrs.toFixed(0) : hrs.toFixed(2)}</span>
                      : <span className="text-muted-foreground/30">-</span>
                    }
                  </td>
                ))}
                {/* Row total - sticky: show budgetHours from API */}
                <td className="text-right tabular-nums px-4 py-2.5 font-semibold text-xs
                               sticky right-0 bg-background
                               text-[var(--accent-teal-700)] dark:text-[var(--accent-teal-300)]
                               z-10 border-l border-border/60">
                  {row.budgetedHours % 1 === 0 ? row.budgetedHours.toFixed(0) : row.budgetedHours.toFixed(2)}h
                </td>
              </tr>
            ))}
          </tbody>

          {/* Totals footer */}
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/30">
              <td className="px-4 py-2.5 text-[11px] font-bold sticky left-0 bg-muted z-10
                             border-r border-border/60 whitespace-nowrap">
                Monthly Total
              </td>
              {colTotals.map((total, i) => (
                <td key={months[i]} className="text-center tabular-nums px-3 py-2.5 text-[11px] font-bold">
                  {total > 0
                    ? (total % 1 === 0 ? total.toFixed(0) : total.toFixed(2))
                    : '-'
                  }
                </td>
              ))}
              <td className="text-right tabular-nums px-4 py-2.5 text-[11px] font-bold
                             sticky right-0 bg-muted z-10 border-l border-border/60">
                {budgetGrandTotal % 1 === 0 ? budgetGrandTotal.toFixed(0) : budgetGrandTotal.toFixed(2)}h
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// --- Main component ---

export function ResourceBudgetTab({
  project,
  budgetData,
  budgetLoading,
  hasLiveBudget,
}: ResourceBudgetTabProps) {
  const [showMonthly, setShowMonthly] = useState(hasLiveBudget);

  // Auto-expand monthly breakdown when live budget data arrives with monthly hours
  useEffect(() => {
    if (hasLiveBudget && budgetData?.roleBudgets?.some((r) => r.monthlyHours)) {
      setShowMonthly(true);
    }
  }, [hasLiveBudget, budgetData]);

  // -- Normalise rows --
  const rows = useMemo<NormalisedRoleRow[]>(() => {
    if (hasLiveBudget && budgetData?.roleBudgets?.length) {
      return budgetData.roleBudgets.map((r) => ({
        id: String(r.id),
        role: r.roleName ?? 'Unknown Role',
        budgetedHours: r.budgetHours,
        actualHours: r.actualHours,
        remainingHours: r.remainingHours,
        costRate: r.budgetCost > 0 && r.budgetHours > 0 ? r.budgetCost / r.budgetHours : 0,
        billingRate: r.billRate,
        monthlyHoursRaw: r.monthlyHours,
      }));
    }

    // Demo data - group budgetItems by role, attach monthly map
    const roleBudgetMap = new Map<string, Record<string, number>>();
    (project.roleBudgetHours ?? []).forEach(r => roleBudgetMap.set(r.role, r.monthlyHours));

    const byRole = new Map<string, NormalisedRoleRow>();
    project.budgetItems.forEach((item: ProjectBudgetItem) => {
      const existing = byRole.get(item.role);
      if (existing) {
        existing.budgetedHours  += item.budgetedHours;
        existing.actualHours    += item.actualHours;
        existing.remainingHours += item.remainingHours;
      } else {
        byRole.set(item.role, {
          id: item.id,
          role: item.role,
          budgetedHours:  item.budgetedHours,
          actualHours:    item.actualHours,
          remainingHours: item.remainingHours,
          costRate:        item.costRate,
          billingRate:     item.billingRate,
          monthlyHoursMap: roleBudgetMap.get(item.role),
        });
      }
    });
    return Array.from(byRole.values());
  }, [project.budgetItems, project.roleBudgetHours, budgetData, hasLiveBudget]);

  // -- Month columns: buildProjectMonths with fallback to monthlyHours array length --
  // Mirrors EBProjects: months = buildProjectMonthsRb(projectStartDate, projectEndDate)
  const months = useMemo<string[]>(() => {
    // First determine the longest monthlyHours array from live data
    let maxArrayLen = 0;
    if (hasLiveBudget && budgetData?.roleBudgets?.length) {
      budgetData.roleBudgets.forEach((r) => {
        if (r.monthlyHours) {
          try {
            const parsed = JSON.parse(r.monthlyHours);
            if (Array.isArray(parsed) && parsed.length > maxArrayLen) {
              maxArrayLen = parsed.length;
            }
          } catch { /* ignore */ }
        }
      });
    }

    // Try project dates
    const fromDates = buildProjectMonths(project.startDate, project.endDate);

    // Use whichever is longer: date-derived months or monthlyHours array length
    if (fromDates.length >= maxArrayLen) {
      return fromDates;
    }

    // monthlyHours array is longer - build months from current year with that length
    const year = new Date().getFullYear();
    return Array.from({ length: maxArrayLen }, (_, i) =>
      `${year}-${String(i + 1).padStart(2, '0')}`,
    );
  }, [project.startDate, project.endDate, budgetData, hasLiveBudget]);

  // -- Totals --
  const totals = useMemo(() => {
    const budgeted  = rows.reduce((s, r) => s + r.budgetedHours,  0);
    const actual    = rows.reduce((s, r) => s + r.actualHours,    0);
    const remaining = rows.reduce((s, r) => s + r.remainingHours, 0);
    return { budgeted, actual, remaining, pct: pctUsed(actual, budgeted) };
  }, [rows]);

  // -- Loading --
  if (budgetLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-xs text-muted-foreground">Loading budget data...</p>
      </div>
    );
  }

  // -- Empty --
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <BarChart3 className="h-5 w-5 text-muted-foreground/40" />
        </div>
        <p className="text-sm font-medium">No budget data available</p>
        <p className="text-xs text-muted-foreground">
          Resource budget items are managed in the Projects application.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl">

      {/* -- KPI strip -- */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center divide-x divide-border">
            <div className="flex-1 px-5 first:pl-4">
              <KPI variant="mini" label="Budget (h)"
                value={totals.budgeted.toLocaleString()}
                description="Total budgeted hours"
                icon={<Clock className="w-4 h-4" />} accent="info" />
            </div>
            <div className="flex-1 px-5">
              <KPI variant="mini" label="Allocated (h)"
                value={totals.actual.toLocaleString()}
                description={`${totals.pct}% of budget consumed`}
                icon={<TrendingUp className="w-4 h-4" />}
                accent={totals.pct >= 100 ? 'destructive' : totals.pct >= 85 ? 'warning' : 'success'} />
            </div>
            <div className="flex-1 px-5">
              <KPI variant="mini" label="Remaining (h)"
                value={totals.remaining.toLocaleString()}
                description="Hours still available"
                icon={<CheckCircle2 className="w-4 h-4" />}
                accent={totals.remaining <= 0 ? 'destructive' : 'teal'} />
            </div>
            <div className="flex-1 px-5 last:pr-4">
              <KPI variant="mini" label="% Used"
                value={`${totals.pct}%`}
                description={totals.pct >= 85 ? 'Approaching limit' : 'Within budget'}
                icon={<AlertCircle className="w-4 h-4" />}
                accent={totals.pct >= 100 ? 'destructive' : totals.pct >= 85 ? 'warning' : 'success'} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* -- Budget utilisation bar -- */}
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Budget Utilisation
          </span>
          <span className="text-xs font-semibold tabular-nums">
            {totals.actual.toLocaleString()}h / {totals.budgeted.toLocaleString()}h
          </span>
        </div>
        <Progress value={Math.min(totals.pct, 100)} className="h-2" />
        <div className="flex justify-between mt-1.5">
          <span className="text-[10px] text-muted-foreground">0h</span>
          <span className="text-[10px] text-muted-foreground">{totals.budgeted.toLocaleString()}h</span>
        </div>
      </div>

      {/* -- Role budgets table -- */}
      <div className="rounded-xl border border-border overflow-hidden">

        {/* Table toolbar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 border-b border-border">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Budget by Role
          </span>
          {months.length > 0 && (
            <Button
              variant={showMonthly ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => setShowMonthly(v => !v)}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {showMonthly ? 'Hide monthly breakdown' : 'Show monthly breakdown'}
            </Button>
          )}
        </div>

        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground h-10 pl-4">
                Role
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground h-10 text-right">
                Budget (h)
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground h-10 text-right">
                Actual (h)
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground h-10 text-right">
                Remaining (h)
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground h-10 min-w-[120px]">
                % Used
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground h-10 pr-4">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const pct = pctUsed(row.actualHours, row.budgetedHours);
              return (
                <TableRow key={row.id} className="border-b border-border/60 hover:bg-muted/20 transition-colors">
                  <TableCell className="py-3 pl-4">
                    <span className="text-sm font-medium">{row.role}</span>
                  </TableCell>
                  <TableCell className="py-3 text-right tabular-nums text-sm font-medium">
                    {row.budgetedHours % 1 === 0
                      ? row.budgetedHours.toFixed(0)
                      : row.budgetedHours.toFixed(2)}
                  </TableCell>
                  <TableCell className="py-3 text-right tabular-nums text-sm">
                    {row.actualHours % 1 === 0
                      ? row.actualHours.toFixed(0)
                      : row.actualHours.toFixed(2)}
                  </TableCell>
                  <TableCell className="py-3 text-right tabular-nums text-sm">
                    {row.remainingHours % 1 === 0
                      ? row.remainingHours.toFixed(0)
                      : row.remainingHours.toFixed(2)}
                  </TableCell>
                  <TableCell className="py-3">
                    <PctBar pct={pct} />
                  </TableCell>
                  <TableCell className="py-3 pr-4">
                    <StatusBadge pct={pct} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          {/* Totals footer */}
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/30">
              <td className="pl-4 py-3 text-sm font-bold">TOTAL</td>
              <td className="py-3 text-right tabular-nums text-sm font-bold pr-3">
                {totals.budgeted % 1 === 0 ? totals.budgeted.toFixed(0) : totals.budgeted.toFixed(2)}
              </td>
              <td className="py-3 text-right tabular-nums text-sm font-bold pr-3">
                {totals.actual % 1 === 0 ? totals.actual.toFixed(0) : totals.actual.toFixed(2)}
              </td>
              <td className="py-3 text-right tabular-nums text-sm font-bold pr-3">
                {totals.remaining % 1 === 0 ? totals.remaining.toFixed(0) : totals.remaining.toFixed(2)}
              </td>
              <td className="py-3"><PctBar pct={totals.pct} /></td>
              <td className="py-3 pr-4" />
            </tr>
          </tfoot>
        </Table>
      </div>

      {/* -- Unified monthly grid - shown when toggled -- */}
      {showMonthly && months.length > 0 && (
        <UnifiedMonthlyGrid rows={rows} months={months} />
      )}

    </div>
  );
}
